import { vmError } from './errors.js';
import { DEFAULT_EXEC_TIMEOUT_MS, MAX_EXEC_TIMEOUT_MS, MAX_REQUEST_BYTES, READY_LINE, REPLY_MAGIC, REQUEST_MAGIC, SERIAL_EXEC, WORKSPACE_ROOT, } from './types.js';
/**
 * Host side of the guest agent protocol, ported from spike/web/exec-channel.js.
 * The guest side is the image's `eosd`, a bash loop on /dev/ttyS1 started by
 * inittab as the learner.
 *
 *   host -> guest   EOS1 <id> <cwd-b64> <env-b64|-> <script-b64> <stdin-b64|->
 *   guest -> host   EOSR <id> <code> <stdout-b64> <stderr-b64> <capped>
 *
 * One request is in flight at a time and the rest queue in order: the agent
 * is a single loop, and the server's terminal gateway serialised git state
 * extraction the same way. Everything here uses only globals a browser and
 * Node both have, so the headless state builder can drive the same channel.
 *
 * ## Why a request is bounded, and what happens when the guest goes quiet
 *
 * The line has no flow control in either direction and the agent reads a
 * request with bash's `read`, one `read(2)` per byte. Measured against the
 * real image, a request line of about 131 KB arrives intact and one of about
 * 175 KB stops ttyS1 for good: the guest emits nothing ever again, a bare
 * newline does not revive it, and `ready()` fails. Since every `Workspace`
 * call travels this line, the tab is then dead while the learner's shell on
 * ttyS0 still echoes. So `exec` refuses a request over `MAX_REQUEST_BYTES`
 * instead of sending it, and a caller with more to say splits it into
 * several — the reply to each is the flow control the wire does not have.
 *
 * When a request does time out, the channel finds out which of the two
 * things happened. A busy agent is not a broken one: it is a single loop and
 * a script of its own may still be running, so answering late is correct.
 * What a healthy agent cannot do is stay silent past its own `timeout 120`
 * backstop. So a timeout sends a lone newline (which closes a line the agent
 * may be holding half-read) and then probes quietly until either a byte
 * arrives or that backstop has expired. Silence past it is a machine that is
 * never coming back: everything outstanding fails, every later `exec` fails
 * at once rather than hanging for its own timeout, and `onDead` hands the
 * app the one thing it can still do about it — boot another VM.
 */
const encoder = new TextEncoder();
const decoder = new TextDecoder();
const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());
export function serialByteEvent(port) {
    return `serial${port}-output-byte`;
}
function toBase64(bytes) {
    let s = '';
    // fromCharCode.apply blows the argument limit on a large output, so the
    // bytes go in 32 KB slices.
    for (let i = 0; i < bytes.length; i += 0x8000)
        s += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
    return btoa(s);
}
function fromBase64(s) {
    const bin = atob(s);
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++)
        out[i] = bin.charCodeAt(i);
    return out;
}
const b64 = (text) => toBase64(encoder.encode(text));
const unb64 = (text) => decoder.decode(fromBase64(text));
/** `export K='v'` lines, single quotes escaped, for the agent's `set -a; . env`. */
function envScript(env) {
    return Object.entries(env)
        .map(([k, v]) => `export ${k}='${String(v).replace(/'/g, `'\\''`)}'`)
        .join('\n');
}
/**
 * Never let noise on the line grow unbounded (kernel messages, a runaway
 * guest). The agent caps each stream at 1 MiB, so the longest reply it can
 * frame is about 2.8 MB of base64 and anything past this guard is not a
 * reply at all.
 */
const MAX_LINE_BYTES = 8_000_000;
/**
 * Room the size check leaves for the id, which is only assigned when the
 * request reaches the front of the queue. Ids are a decimal counter or a
 * probe ("p7"), so this is orders of magnitude more than one needs.
 */
const MAX_ID_BYTES = 16;
/**
 * How often the health watch probes a silent guest. Slowly, because a guest
 * that is merely busy has to grind through every probe before it reaches the
 * real work behind them, at a fork and two execs each.
 */
const HEALTH_PROBE_INTERVAL_MS = 10_000;
/**
 * Added to the guest agent's own backstop before the health watch gives up:
 * the round trip and the reply itself, both on an emulator that is by
 * definition having a bad day.
 */
const HEALTH_SLACK_MS = 10_000;
/** What one request takes on the line: magic, id, payloads, spaces, newline. */
function requestBytes(fields) {
    let bytes = REQUEST_MAGIC.length + 1 + MAX_ID_BYTES + 1;
    // Base64 and the magic are ASCII, so a JavaScript length is a byte count.
    for (const field of fields)
        bytes += field.length + 1;
    return bytes;
}
export class ExecChannel {
    emulator;
    port;
    line = '';
    queue = [];
    current = null;
    seq = 0;
    sawReady = false;
    destroyed = false;
    died = false;
    /** True from an overflow until the end of the line that caused it. */
    dropping = false;
    /** Set only while `ready()` is probing; probes travel outside the queue. */
    onProbeAnswered = null;
    /** Probes outside the queue need ids of their own; `seq` numbers requests. */
    probeSeq = 0;
    /** Any byte at all on this line is the guest alive. Nothing else is. */
    lastByteAt = now();
    /** Set only while a timed-out guest is being watched (see `watchHealth`). */
    watch = null;
    /**
     * Requests this host stopped waiting for that the guest is still running,
     * by id, with the moment its own `timeout 120` must have ended them.
     *
     * The agent is one loop: until it has answered one of these it reads
     * nothing else, so a request sent meanwhile would spend its whole timeout
     * queued behind a script nobody wants. Measured 2026-10-01 at a sixth of
     * this laptop's speed: the warm-up overran its 60 s, the level's unpack was
     * sent at once, waited out its own 60 s behind the warm-up, and the learner
     * got "This level could not be opened" on a machine that was merely slow.
     * So the next request is held here, clock not started, until the late
     * reply arrives or that backstop has passed.
     */
    orphans = new Map();
    orphanTimer = null;
    deadListeners = new Set();
    constructor(emulator, port = SERIAL_EXEC) {
        this.emulator = emulator;
        this.port = port;
        emulator.add_listener(serialByteEvent(port), (byte) => this.onByte(byte));
    }
    /** True once the guest agent has stopped answering. Only a new VM recovers. */
    get dead() {
        return this.died;
    }
    /**
     * Called with the error every later `exec` will reject with, once the guest
     * agent has stopped answering. Returns the unsubscribe.
     *
     * This is the failure's only way upward. Without it the app sits on the
     * play screen with every action hanging for its own timeout and then
     * blaming itself, and the boot screen's sentence for a machine that
     * stopped answering is never shown again.
     */
    onDead(cb) {
        this.deadListeners.add(cb);
        return () => void this.deadListeners.delete(cb);
    }
    onByte(byte) {
        this.lastByteAt = now();
        if (byte === 10) {
            const line = this.line;
            this.line = '';
            // The rest of a line that already overflowed is not a message.
            if (this.dropping) {
                this.dropping = false;
                return;
            }
            // The guest's tty is raw, so a stray CR may still precede the LF.
            this.onLine(line.replace(/\r$/, ''));
            return;
        }
        if (this.dropping || byte === 13)
            return;
        this.line += String.fromCharCode(byte);
        if (this.line.length > MAX_LINE_BYTES)
            this.overflow();
    }
    /**
     * Throwing the accumulator away is not enough on its own: the bytes that
     * already arrived carried the reply the request in flight was waiting for,
     * and nothing will send it again, so the request would sit there until its
     * timeout — up to two minutes — and then blame the guest for being slow.
     * Fail it now, say what actually happened, and skip the rest of the line
     * so the same flood cannot go on to kill the requests behind it.
     */
    overflow() {
        this.line = '';
        this.dropping = true;
        const current = this.current;
        if (!current)
            return;
        clearTimeout(current.timer);
        this.current = null;
        current.reject(vmError('protocol', `the guest sent more than ${MAX_LINE_BYTES} bytes on one line, which its output caps should` +
            ` make impossible; the image and packages/vm may be from different builds: ${short(current.script)}`));
        this.pump();
    }
    onLine(line) {
        if (line === READY_LINE) {
            this.sawReady = true;
            return;
        }
        // Anything else that is not a reply is kernel noise from before the
        // agent started, or the agent's own diagnostics.
        if (!line.startsWith(`${REPLY_MAGIC} `))
            return;
        const [, id, code, out = '', err = '', capped = '-'] = line.split(' ');
        if (id === undefined || code === undefined)
            return;
        if (id.startsWith('p')) {
            this.onProbeAnswered?.();
            return;
        }
        const current = this.current;
        if (!current || current.id !== id) {
            // A reply to a request that already timed out: the guest is free again.
            if (this.orphans.delete(id)) {
                this.guestBusy(); // drops the backstop timer once nothing is outstanding
                this.pump();
            }
            return;
        }
        clearTimeout(current.timer);
        this.current = null;
        // "-" (or, from an image that predates the field, nothing) means neither
        // stream was cut; "o", "e" and "oe" name the ones that were.
        const truncated = capped === 'o' || capped === 'e' || capped === 'oe';
        current.resolve({
            code: Number(code),
            stdout: unb64(out),
            stderr: unb64(err),
            ms: now() - current.t0,
            truncated,
        });
        this.pump();
    }
    /**
     * Run `script` with bash in the guest, as the learner. Never rejects on a
     * non-zero exit — that is `code` — only on timeout, a request too large
     * for one line, a dead agent or a destroyed VM.
     */
    exec(script, opts = {}) {
        const timeoutMs = opts.timeoutMs ?? DEFAULT_EXEC_TIMEOUT_MS;
        if (this.destroyed) {
            return Promise.reject(vmError('destroyed', `the VM was destroyed: ${short(script)}`));
        }
        if (this.died) {
            return Promise.reject(vmError('dead', `the guest agent is not answering: ${short(script)}`));
        }
        if (timeoutMs > MAX_EXEC_TIMEOUT_MS) {
            // The agent runs every script under `timeout 120` and the wire has no
            // field for a per-request timeout, so waiting longer waits for a reply
            // the guest has already stopped trying to produce.
            return Promise.reject(vmError('timeout', `timeoutMs ${timeoutMs} exceeds the guest agent's own backstop of ${MAX_EXEC_TIMEOUT_MS} ms`));
        }
        // Encoded here rather than in `pump` so the size is known before the
        // caller is given a promise: a request too large to frame is a mistake to
        // report, not a machine to break.
        const env = opts.env ?? {};
        const input = opts.input ?? '';
        const fields = [
            b64(opts.cwd ?? WORKSPACE_ROOT),
            Object.keys(env).length > 0 ? b64(envScript(env)) : '-',
            b64(script),
            input ? b64(input) : '-',
        ];
        const bytes = requestBytes(fields);
        if (bytes > MAX_REQUEST_BYTES) {
            return Promise.reject(vmError('payload', `this request needs ${bytes} bytes on one line and the guest's serial port takes at most` +
                ` ${MAX_REQUEST_BYTES} (${fields[3] === '-' ? 'none' : fields[3].length} of them stdin);` +
                ` send it in pieces: ${short(script)}`));
        }
        return new Promise((resolve, reject) => {
            this.queue.push({ script, fields, timeoutMs, resolve, reject });
            this.pump();
        });
    }
    pump() {
        if (this.current || this.queue.length === 0 || this.destroyed || this.died)
            return;
        if (this.guestBusy())
            return;
        const req = this.queue.shift();
        const id = String(++this.seq);
        const t0 = now();
        const timer = setTimeout(() => {
            if (this.current?.id !== id)
                return;
            // Give the channel back rather than waiting on a reply nobody is
            // waiting for: the agent will answer with an id we no longer know.
            this.current = null;
            this.orphans.set(id, t0 + MAX_EXEC_TIMEOUT_MS + HEALTH_SLACK_MS);
            req.reject(vmError('timeout', `exec timed out after ${req.timeoutMs} ms: ${short(req.script)}`));
            this.resync();
            this.watchHealth(t0);
            this.pump();
        }, req.timeoutMs);
        this.current = { id, script: req.script, timer, t0, resolve: req.resolve, reject: req.reject };
        this.send([REQUEST_MAGIC, id, ...req.fields]);
    }
    /** Whether a timed-out request may still be running; if so, arrange to pump when its backstop passes. */
    guestBusy() {
        const t = now();
        for (const [id, deadline] of this.orphans)
            if (deadline <= t)
                this.orphans.delete(id);
        if (this.orphans.size === 0) {
            if (this.orphanTimer !== null)
                clearTimeout(this.orphanTimer);
            this.orphanTimer = null;
            return false;
        }
        if (this.orphanTimer === null) {
            const next = Math.min(...this.orphans.values());
            this.orphanTimer = setTimeout(() => {
                this.orphanTimer = null;
                this.pump();
            }, Math.max(0, next - t));
        }
        return true;
    }
    send(fields) {
        this.emulator.serial_send_bytes(this.port, encoder.encode(`${fields.join(' ')}\n`));
    }
    /**
     * Close a line the agent may be holding half-read.
     *
     * A request whose newline never arrived leaves `eosd` blocked in `read`,
     * and the next request would only be appended to what it is already
     * holding. A lone newline ends that line, and the agent skips it, because
     * its magic is not EOS1.
     *
     * It does not cure the failure `MAX_REQUEST_BYTES` exists for: measured
     * against the real image, once an oversized line has stopped ttyS1 nothing
     * sent afterwards reaches the guest at all, this newline included. That is
     * what the health watch below is for.
     */
    resync() {
        this.emulator.serial_send_bytes(this.port, encoder.encode('\n'));
    }
    /**
     * After a timeout, work out whether the guest is busy or gone.
     *
     * A timeout on its own says nothing. The agent is one loop, a script of its
     * own may still be running, and answering late is correct: a `sleep 6`
     * under a 1500 ms timeout replies 5.4 s later and the channel is healthy.
     * What a healthy agent cannot do is stay silent past its own backstop —
     * every script runs under `timeout 120`, so within `MAX_EXEC_TIMEOUT_MS` of
     * a request starting it has finished and replied, even to an id this host
     * has already forgotten. So probe until any byte arrives, and give up at
     * that deadline, measured from the request rather than from the timeout
     * because a short timeout does not shorten the guest's backstop.
     */
    watchHealth(requestStartedAt) {
        if (this.destroyed || this.died)
            return;
        const deadline = requestStartedAt + MAX_EXEC_TIMEOUT_MS + HEALTH_SLACK_MS;
        if (this.watch) {
            // Already watching: the later request's backstop is the one that has
            // to expire before the agent can be called dead.
            this.watch.deadline = Math.max(this.watch.deadline, deadline);
            return;
        }
        this.watch = { timer: setInterval(() => this.checkHealth(), HEALTH_PROBE_INTERVAL_MS), startedAt: now(), deadline };
        this.probe();
    }
    checkHealth() {
        const watch = this.watch;
        if (!watch)
            return;
        if (this.destroyed || this.died) {
            this.stopWatch();
            return;
        }
        if (this.lastByteAt > watch.startedAt) {
            this.stopWatch(); // the guest spoke: late, busy, alive
            return;
        }
        if (now() > watch.deadline) {
            this.die(`the guest agent has said nothing for ${Math.round(now() - watch.startedAt)} ms, which is past its own` +
                ` ${MAX_EXEC_TIMEOUT_MS} ms backstop: this machine will not answer again`);
            return;
        }
        this.probe();
    }
    stopWatch() {
        if (!this.watch)
            return;
        clearInterval(this.watch.timer);
        this.watch = null;
    }
    /**
     * A no-op request sent outside the queue. Ids beginning with "p" are
     * probes: the reply carries no result, and `ready()` and the health watch
     * both take any of them as an answer — the second through `lastByteAt`,
     * because for a health check the bytes are the whole message.
     */
    probe() {
        this.send([REQUEST_MAGIC, `p${++this.probeSeq}`, b64('/'), '-', b64('true'), '-']);
    }
    /**
     * The agent has stopped answering: fail everything, refuse everything
     * after it, and tell whoever is listening. Nothing here recovers a VM;
     * `onDead` exists so the app can boot another one.
     */
    /** Stop the command channel when another device has made the VM unusable. */
    fail(message) { this.die(message); }
    die(message) {
        if (this.died || this.destroyed)
            return;
        this.died = true;
        this.stopWatch();
        if (this.orphanTimer !== null)
            clearTimeout(this.orphanTimer);
        const err = vmError('dead', message);
        if (this.current) {
            clearTimeout(this.current.timer);
            this.current.reject(err);
            this.current = null;
        }
        for (const req of this.queue.splice(0))
            req.reject(err);
        for (const cb of [...this.deadListeners])
            cb(err);
    }
    /**
     * Resolves (with the wait in ms) once the agent answers a no-op.
     *
     * On a cold boot the agent announces itself with EOSREADY; wait for that
     * first, because probing earlier only queues requests in the tty buffer
     * for the agent to grind through once it starts. Then probe outside the
     * request queue, once a second, until any probe is answered. A restored
     * guest drops the first bytes it receives until more arrive, and its first
     * command runs on a cold JIT; a lost or slow probe must not cost a fixed
     * wait, so no single probe has a timeout of its own. This is what took
     * restore-to-ready from 16 s to under 2 s in phase 0.
     */
    async ready(opts = {}) {
        const { restored = false, timeoutMs = 180_000 } = opts;
        const t0 = now();
        if (!restored) {
            while (!this.sawReady) {
                if (this.destroyed)
                    throw vmError('destroyed', 'the VM was destroyed while it was booting');
                if (this.died)
                    throw vmError('dead', 'the guest agent is not answering');
                if (now() - t0 > timeoutMs)
                    throw vmError('boot', 'the guest agent never announced itself');
                await new Promise((r) => setTimeout(r, 50));
            }
        }
        if (this.died)
            throw vmError('dead', 'the guest agent is not answering');
        return new Promise((resolve, reject) => {
            const probe = () => this.probe();
            const finish = () => {
                clearInterval(timer);
                this.onProbeAnswered = null;
            };
            const timer = setInterval(() => {
                if (this.destroyed) {
                    finish();
                    reject(vmError('destroyed', 'the VM was destroyed while it was booting'));
                    return;
                }
                if (now() - t0 > timeoutMs) {
                    finish();
                    // `Vm.ready()` is how the app asks whether the machine is still
                    // there. An answer of "no" has to reach the same terminal state a
                    // timed-out request does, or it is a rejection nobody acts on.
                    this.die('the guest agent did not answer a readiness probe: this machine will not answer again');
                    reject(vmError('boot', 'the guest agent did not answer'));
                    return;
                }
                probe();
            }, 1000);
            this.onProbeAnswered = () => {
                finish();
                resolve(now() - t0);
            };
            probe();
        });
    }
    /** Fail everything outstanding. The emulator is gone; nothing will ever reply. */
    destroy() {
        if (this.destroyed)
            return;
        this.destroyed = true;
        this.stopWatch();
        if (this.orphanTimer !== null)
            clearTimeout(this.orphanTimer);
        const err = vmError('destroyed', 'the VM was destroyed with work outstanding');
        if (this.current) {
            clearTimeout(this.current.timer);
            this.current.reject(err);
            this.current = null;
        }
        for (const req of this.queue.splice(0))
            req.reject(err);
        this.onProbeAnswered = null;
        this.deadListeners.clear();
    }
}
function short(script) {
    return script.length > 60 ? `${script.slice(0, 60)}…` : script;
}
//# sourceMappingURL=channel.js.map